import { useEffect, useState } from "react";
import axios from "../api/client";
import { useTranslation } from "react-i18next";

function editState(user) {
  return {
    email: user.email,
    role: user.role,
    is_active: Boolean(user.is_active),
    phone_number: user.phone_number || "",
    resetPassword: "",
  };
}

export default function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [edits, setEdits] = useState({});
  const [formState, setFormState] = useState({ email: "", password: "", role: "user", phone_number: "", sendSetupLink: false });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  async function loadUsers() {
    const res = await axios.get("/api/admin/users");
    setUsers(res.data);
    setEdits(Object.fromEntries(res.data.map((user) => [user.id, editState(user)])));
  }

  useEffect(() => {
    loadUsers().catch(() => setError(t("admin.users.errors.load")));
  }, [t]);

  function showError(err, fallback) {
    setError(err.response?.data?.message || fallback);
    setMessage("");
  }

  function updateEdit(id, field, value) {
    setEdits((previous) => ({ ...previous, [id]: { ...previous[id], [field]: value } }));
  }

  async function handleCreate(event) {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      await axios.post("/api/admin/users", formState);
      setFormState({ email: "", password: "", role: "user", phone_number: "", sendSetupLink: false });
      await loadUsers();
      setMessage(t("admin.users.accountCreated"));
    } catch (err) {
      showError(err, t("admin.users.errors.create"));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(user) {
    setError("");
    setMessage("");
    setSavingId(user.id);
    try {
      const edit = edits[user.id];
      const payload = {};
      if (edit.email !== user.email) payload.email = edit.email;
      if (edit.role !== user.role) payload.role = edit.role;
      if (edit.is_active !== Boolean(user.is_active)) payload.is_active = edit.is_active;
      if ((edit.phone_number || "") !== (user.phone_number || "")) payload.phone_number = edit.phone_number || null;
      if (edit.resetPassword) payload.resetPassword = edit.resetPassword;
      if (Object.keys(payload).length === 0) {
        setMessage(t("admin.users.noChanges"));
        return;
      }
      await axios.patch(`/api/admin/users/${user.id}`, payload);
      await loadUsers();
      setMessage(t("admin.users.accountUpdated"));
    } catch (err) {
      showError(err, t("admin.users.errors.update"));
    } finally {
      setSavingId(null);
    }
  }

  async function handleResetPassword(user) {
    const password = edits[user.id]?.resetPassword;
    if (!password) {
      setError(t("admin.users.errors.passwordRequired"));
      return;
    }
    setError("");
    setMessage("");
    setSavingId(user.id);
    try {
      await axios.post(`/api/admin/users/${user.id}/reset-password`, { password });
      await loadUsers();
      setMessage(t("admin.users.passwordReset"));
    } catch (err) {
      showError(err, t("admin.users.errors.resetPassword"));
    } finally {
      setSavingId(null);
    }
  }

  async function handleRevokeSessions(user) {
    setError("");
    setMessage("");
    setSavingId(user.id);
    try {
      await axios.post(`/api/admin/users/${user.id}/revoke-sessions`);
      setMessage(t("admin.users.sessionsRevoked"));
    } catch (err) {
      showError(err, t("admin.users.errors.revokeSessions"));
    } finally {
      setSavingId(null);
    }
  }

  async function handleRemoveTestAccount(user) {
    if (!window.confirm(t("admin.users.confirmRemoveTestAccount", { email: user.email }))) return;
    setError("");
    setMessage("");
    setSavingId(user.id);
    try {
      await axios.delete(`/api/admin/users/${user.id}/development-test-account`);
      await loadUsers();
      setMessage(t("admin.users.testAccountRemoved"));
    } catch (err) {
      showError(err, t("admin.users.errors.removeTestAccount"));
    } finally {
      setSavingId(null);
    }
  }

  async function handleSetupAction(user, action) {
    setError(""); setMessage(""); setSavingId(user.id);
    try {
      if (action === "resend") await axios.post(`/api/admin/users/${user.id}/password-setup`);
      else await axios.delete(`/api/admin/users/${user.id}/password-setup`);
      await loadUsers();
      setMessage(t(action === "resend" ? "admin.users.setupSent" : "admin.users.setupRevoked"));
    } catch (err) { showError(err, t("admin.users.errors.setup")); }
    finally { setSavingId(null); }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-white border rounded-xl shadow p-4">
        <h1 className="text-2xl font-semibold mb-4">{t("admin.users.title")}</h1>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.users.email")}
            <input type="email" value={formState.email} onChange={(e) => setFormState((p) => ({ ...p, email: e.target.value }))} className="w-full border rounded-lg px-3 py-2 mt-1" required />
          </label>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.users.password")}
            <input type="password" minLength="12" value={formState.password} onChange={(e) => setFormState((p) => ({ ...p, password: e.target.value }))} className="w-full border rounded-lg px-3 py-2 mt-1" required={!formState.sendSetupLink} disabled={formState.sendSetupLink} />
          </label>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.users.role")}
            <select value={formState.role} onChange={(e) => setFormState((p) => ({ ...p, role: e.target.value }))} className="w-full border rounded-lg px-3 py-2 mt-1 bg-white">
              <option value="user">{t("admin.users.user")}</option>
              <option value="admin">{t("admin.users.administrator")}</option>
            </select>
          </label>
          <label className="flex gap-2 items-center text-sm font-medium text-gray-700 md:col-span-4">
            <input type="checkbox" checked={formState.sendSetupLink} onChange={(e) => setFormState((p) => ({ ...p, sendSetupLink: e.target.checked, password: e.target.checked ? "" : p.password }))} />
            {t("admin.users.sendSetupLink")}
          </label>
          <label className="block text-sm font-medium text-gray-600">
            {t("admin.users.phone")}
            <input type="tel" value={formState.phone_number} onChange={(e) => setFormState((p) => ({ ...p, phone_number: e.target.value }))} className="w-full border rounded-lg px-3 py-2 mt-1" placeholder={t("admin.users.phonePlaceholder")} />
          </label>
          <div className="md:col-span-4">
            <button type="submit" disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60">
              {loading ? t("admin.users.loading") : t("admin.users.add")}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white border rounded-xl shadow p-4">
        <h2 className="text-xl font-semibold mb-3">{t("admin.users.existing")}</h2>
        {error && <p className="text-red-600 text-sm mb-3" role="alert">{error}</p>}
        {message && <p className="text-green-700 text-sm mb-3">{message}</p>}
        <div className="space-y-4">
          {users.map((user) => {
            const edit = edits[user.id] || editState(user);
            const saving = savingId === user.id;
            const canRemoveTestAccount = import.meta.env.DEV && user.email.endsWith("@example.test");
            return (
              <section key={user.id} className="border rounded-lg p-3" aria-label={user.email}>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("admin.users.email")}
                    <input type="email" value={edit.email} onChange={(e) => updateEdit(user.id, "email", e.target.value)} className="w-full border rounded px-2 py-1 mt-1" required />
                  </label>
                  <label className="block text-sm font-medium text-gray-600">
                    {t("admin.users.role")}
                    <select value={edit.role} onChange={(e) => updateEdit(user.id, "role", e.target.value)} className="w-full border rounded px-2 py-1 mt-1 bg-white">
                      <option value="user">{t("admin.users.user")}</option>
                      <option value="admin">{t("admin.users.administrator")}</option>
                    </select>
                  </label>
                  <label className="block text-sm font-medium text-gray-600">
                    {t("admin.users.phone")}
                    <input type="tel" value={edit.phone_number} onChange={(e) => updateEdit(user.id, "phone_number", e.target.value)} className="w-full border rounded px-2 py-1 mt-1" placeholder={t("admin.users.phonePlaceholder")} />
                  </label>
                  <label className="flex gap-2 items-center text-sm font-medium text-gray-700 pb-2">
                    <input type="checkbox" checked={edit.is_active} onChange={(e) => updateEdit(user.id, "is_active", e.target.checked)} />
                    {t("admin.users.active")}
                  </label>
                  <button type="button" onClick={() => handleSave(user)} disabled={saving} className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 disabled:opacity-60">
                    {saving ? t("admin.users.loading") : t("admin.users.save")}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t items-end">
                  <label className="block text-sm font-medium text-gray-600">
                    {t("admin.users.newPassword")}
                    <input type="password" minLength="12" value={edit.resetPassword} onChange={(e) => updateEdit(user.id, "resetPassword", e.target.value)} className="w-full border rounded px-2 py-1 mt-1" />
                  </label>
                  <button type="button" onClick={() => handleResetPassword(user)} disabled={saving} className="text-blue-700 border border-blue-600 px-3 py-2 rounded hover:bg-blue-50 disabled:opacity-60">
                    {t("admin.users.resetPassword")}
                  </button>
                  <button type="button" onClick={() => handleRevokeSessions(user)} disabled={saving} className="text-gray-700 border border-gray-400 px-3 py-2 rounded hover:bg-gray-50 disabled:opacity-60">
                    {t("admin.users.revokeSessions")}
                  </button>
                  {user.password_setup_pending_at && <>
                    <button type="button" onClick={() => handleSetupAction(user, "resend")} disabled={saving} className="text-blue-700 border border-blue-600 px-3 py-2 rounded hover:bg-blue-50 disabled:opacity-60">{t("admin.users.resendSetup")}</button>
                    <button type="button" onClick={() => handleSetupAction(user, "revoke")} disabled={saving} className="text-gray-700 border border-gray-400 px-3 py-2 rounded hover:bg-gray-50 disabled:opacity-60">{t("admin.users.revokeSetup")}</button>
                  </>}
                  {canRemoveTestAccount && (
                    <button type="button" onClick={() => handleRemoveTestAccount(user)} disabled={saving} className="text-red-700 border border-red-500 px-3 py-2 rounded hover:bg-red-50 disabled:opacity-60">
                      {t("admin.users.removeTestAccount")}
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-3">{t("admin.users.created")}: {new Date(user.created_at).toLocaleString()}</p>
                {user.password_setup_pending_at && <p className="text-xs text-amber-700 mt-1">{t("admin.users.pendingSetup")}</p>}
              </section>
            );
          })}
          {users.length === 0 && <p className="py-4 text-center text-gray-500">{t("admin.users.noUsers")}</p>}
        </div>
      </div>
    </div>
  );
}
